import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { certificates } from '../data/certificates';

const ACCENTS = [
  {
    text: 'text-arcade-cyan',
    border: 'border-arcade-cyan',
    glow: 'hover:shadow-[0_0_18px_rgba(77,232,244,0.35)]',
  },
  {
    text: 'text-arcade-pink',
    border: 'border-arcade-pink',
    glow: 'hover:shadow-[0_0_18px_rgba(255,46,126,0.35)]',
  },
  {
    text: 'text-arcade-yellow',
    border: 'border-arcade-yellow',
    glow: 'hover:shadow-[0_0_18px_rgba(255,201,61,0.35)]',
  },
];

function Certificates() {
  const { t } = useTranslation();

  return (
    <section
      id="certificates"
      className="min-h-screen relative overflow-hidden bg-arcade-bg crt-scanlines py-16 md:py-20 flex items-center"
    >
      {/* faint grid */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.06] pointer-events-none"></div>

      {/* decorative pixels */}
      <div className="absolute top-[12%] left-[10%] w-3 h-3 bg-arcade-yellow"></div>
      <div className="absolute bottom-[15%] right-[9%] w-2.5 h-2.5 bg-arcade-cyan animate-pulse"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        {/* section header */}
        <motion.div
          className="flex items-center gap-6 mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-pixel text-arcade-yellow text-base md:text-xl whitespace-nowrap">
            {t('certificates.achievementsLabel')}
          </h2>
          <div className="h-[2px] flex-1 bg-gradient-to-r from-arcade-yellow to-arcade-yellow/0"></div>
        </motion.div>

        <motion.h3
          className="font-pixel text-arcade-cyan text-2xl md:text-5xl mb-10 md:mb-14 drop-shadow-[0_0_12px_rgba(77,232,244,0.45)]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          {t('certificates.title').toUpperCase()}
        </motion.h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {certificates.map((cert, index) => {
            const accent = ACCENTS[index % ACCENTS.length];

            return (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`border-2 ${accent.border} ${accent.glow} rounded-lg bg-arcade-panel/60 overflow-hidden transition-all duration-200 hover:-translate-y-1 flex flex-col`}
              >
                {/* card header */}
                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                  <span className={`font-pixel ${accent.text} text-sm md:text-base`}>
                    {String(cert.id).padStart(2, '0')}
                  </span>
                  <span className="font-mono text-[10px] md:text-xs text-arcade-green tracking-wider">
                    ★ {t('certificates.unlocked')}
                  </span>
                </div>

                {/* image */}
                <div className="relative h-40 mx-4 mb-4 overflow-hidden rounded border border-white/5 bg-arcade-bg">
                  <img
                    src={cert.image}
                    alt={cert.title}
                    className="w-full h-full object-cover object-top opacity-80 hover:opacity-100 transition-opacity duration-300"
                  />
                </div>

                {/* content */}
                <div className="px-4 pb-5 mt-auto">
                  <h4 className="font-mono font-bold text-white text-sm md:text-base leading-6 mb-1">
                    {cert.title}
                  </h4>
                  <p className="font-mono text-[11px] md:text-xs text-arcade-purple tracking-wider uppercase mb-4">
                    {cert.issuer} · {cert.date}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {cert.skills.slice(0, 3).map((skill) => (
                      <span
                        key={skill}
                        className="font-mono text-[10px] md:text-[11px] text-arcade-cyan border border-arcade-cyan/50 px-2 py-1 tracking-wider"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>

                  <a
                    href={cert.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-block font-pixel text-[9px] md:text-[10px] ${accent.text} border ${accent.border} px-4 py-2.5 rounded hover:bg-white/5 transition-colors`}
                  >
                    ▶ {t('certificates.viewCertificate').toUpperCase()}
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Certificates;
